import React from 'react';

const DAYS_FULL_PT = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MONTHS_PT = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function formatDateLong(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const dayName = DAYS_FULL_PT[dt.getDay()];
    return `${d} de ${MONTHS_PT[m - 1]} de ${y} (${dayName})`;
}

/**
 * RequestSummaryCard — card de resumo com ícones para cada item.
 */
function RequestSummaryCard({ professional, procedures, date, time }) {
    const items = [
        {
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ED3680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
            ),
            label: 'Profissional',
            value: professional?.name,
        },
        {
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ED3680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" />
                    <path d="M12 8v4l3 3" />
                </svg>
            ),
            label: procedures.length > 1 ? 'Serviços' : 'Serviço',
            value: procedures.map(proc => proc.name).join(' + '),
        },
        {
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ED3680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            ),
            label: 'Data',
            value: formatDateLong(date),
        },
        {
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ED3680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            ),
            label: 'Horário',
            value: time,
        },
    ];

    return (
        <div className="ps-summary-card">
            <h3 className="ps-summary-card__title">Resumo da solicitação</h3>
            {items.map((item, i) => (
                <div key={i} className="ps-summary-card__row">
                    <div className="ps-summary-card__icon">{item.icon}</div>
                    <div className="ps-summary-card__content">
                        <span className="ps-summary-card__label">{item.label}</span>
                        <span className="ps-summary-card__value">{item.value}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * StepConfirm — Step 4
 * Dados do paciente + resumo visual + CTA WhatsApp.
 */
export default function StepConfirm({
    professional, procedures, date, time,
    patientName, patientPhone,
    onNameChange, onPhoneChange,
    onBack, onConfirm,
}) {
    const canSubmit = patientName.trim() && patientPhone.trim();

    return (
        <div className="ps-step-content">
            <h2 className="ps-step-title">Seus dados</h2>
            <p className="ps-step-subtitle">Confirme as informações e solicite seu agendamento.</p>

            {/* Resumo */}
            <RequestSummaryCard
                professional={professional}
                procedures={procedures}
                date={date}
                time={time}
            />

            {/* Formulário */}
            <div className="ps-form-card">
                <div className="ps-form__group">
                    <label className="ps-form__label">Nome completo *</label>
                    <input
                        className="ps-form__input"
                        type="text"
                        placeholder="Digite seu nome"
                        value={patientName}
                        onChange={e => onNameChange(e.target.value)}
                        autoComplete="name"
                    />
                </div>
                <div className="ps-form__group">
                    <label className="ps-form__label">WhatsApp *</label>
                    <input
                        className="ps-form__input"
                        type="tel"
                        placeholder="(11) 98999-9999"
                        value={patientPhone}
                        onChange={e => onPhoneChange(e.target.value)}
                        autoComplete="tel"
                    />
                    <span className="ps-form__hint">Usaremos somente para esta solicitação.</span>
                </div>
            </div>

            {/* CTA WhatsApp */}
            <button
                className="ps-btn ps-btn--whatsapp ps-btn--full"
                disabled={!canSubmit}
                onClick={onConfirm}
                type="button"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                </svg>
                Solicitar agendamento no WhatsApp
            </button>

            {/* Voltar */}
            <div className="ps-actions ps-actions--center" style={{ marginTop: 12 }}>
                <button className="ps-btn ps-btn--ghost" onClick={onBack} type="button">
                    ← Voltar
                </button>
            </div>

            <p className="ps-trust-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Seus dados estão protegidos
            </p>
        </div>
    );
}
